import { useState, useEffect } from 'react';
import { apiClient } from '../utils/api';
import ErrorMessage from './ErrorMessage';


const WINDOW_LABELS = {
    weekly: 'Giới hạn theo tuần',
    monthly: 'Giới hạn theo tháng',
    daily: 'Giới hạn theo ngày',
    trial: 'Giới hạn dùng thử',
};

function ProgressBar({ utilization }) {
    const pct = Math.min(utilization, 100);
    const color = pct >= 100 ? 'bg-[#C5C5C7]'
        : pct >= 90 ? 'bg-red-500'
        : pct >= 80 ? 'bg-amber-500'
        : 'bg-brand-blue-200';

    return (
        <div className="w-full h-2 bg-brand-blue-50 rounded-full overflow-hidden">
            <div
                className={`h-full ${color} rounded-full transition-all duration-500`}
                style={{ width: `${pct}%` }}
            />
        </div>
    );
}

function formatResetDate(resetAt) {
    if (!resetAt) return null;
    const d = new Date(resetAt * 1000);
    const hours = d.getHours().toString().padStart(2, '0');
    const mins = d.getMinutes().toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${hours}:${mins} | ${day}/${month}/${year}`;
}

function WindowRow({ window: win }) {
    const label = WINDOW_LABELS[win.type] || win.type;
    const costLimit = win.limits?.cost;
    const reqLimit = win.limits?.requests;
    const maxUtil = Math.max(
        costLimit?.utilization || 0,
        reqLimit?.utilization || 0,
        win.limits?.tokens?.utilization || 0,
    );
    const pct = Math.round(maxUtil);
    const isExceeded = pct >= 100;

    let subText = null;
    if (win.reset_mode === 'one_time') {
        if (win.expires_at) {
            const d = new Date(win.expires_at * 1000);
            const day = d.getDate().toString().padStart(2, '0');
            const month = (d.getMonth() + 1).toString().padStart(2, '0');
            const year = d.getFullYear();
            subText = `Ngày hết hạn ${day}/${month}/${year}`;
        } else if (win.days_remaining !== undefined) {
            subText = `Còn ${win.days_remaining} ngày`;
        }
    } else if (win.reset_at) {
        subText = `Dung lượng sẽ làm mới vào ${formatResetDate(win.reset_at)}`;
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-800">{label}</span>
                <span className={`text-sm font-medium ${isExceeded ? 'text-red-500' : 'text-gray-600'}`}>
                    {pct}%{isExceeded ? ' đã dùng' : ''}
                </span>
            </div>
            <ProgressBar utilization={maxUtil} />
            {subText && (
                <p className="text-xs text-gray-500 italic">
                    {subText}
                </p>
            )}
        </div>
    );
}

function UpgradeBanner({ title, description }) {
    return (
        <div className="bg-brand-blue-50 rounded-xl p-4 space-y-2">
            <p className="text-sm text-gray-700 font-medium">{title}</p>
            <p className="text-sm text-gray-500">{description}</p>
            <a
                href="https://simplize.vn/pricing" target="_blank" rel="noopener noreferrer"
                className="inline-block mt-2 px-4 py-2 text-sm font-medium text-white bg-brand-blue-500 hover:bg-brand-blue-600 rounded-lg transition-colors"
            >
                Nâng cấp Hội viên ngay
            </a>
        </div>
    );
}

const UsagePanel = ({ membershipData }) => {
    const [usageData, setUsageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchUsageData();
    }, []);

    const fetchUsageData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await apiClient.getUsageStatus();
            if (data) {
                setUsageData(data);
            } else {
                setError('Không có dữ liệu sử dụng');
            }
        } catch (err) {
            console.error('Failed to fetch usage data:', err);
            setError('Không thể tải thông tin sử dụng');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-app-hover rounded w-1/3"></div>
                    <div className="h-4 bg-app-hover rounded w-1/4"></div>
                    <div className="h-8 bg-app-hover rounded"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <ErrorMessage message={error} />
            </div>
        );
    }

    if (!usageData) return null;

    const windows = usageData.windows || [];
    const tierExpiresAt = usageData.tier_expires_at;
    const deniedBy = usageData.denied_by;
    const membershipExpired = membershipData?.hasMembership === true && membershipData?.membershipStatus === false;
    const noPlan = deniedBy?.limit_type === 'no_plan';

    return (
        <div className="p-6 space-y-5">
            <h4 className="text-lg font-semibold text-gray-800">Giới hạn sử dụng</h4>

            {tierExpiresAt && !membershipExpired && windows.length > 0 && (
                <p className="text-xs text-gray-500 italic">
                    Gói hiện tại hết hạn vào {formatResetDate(tierExpiresAt)}
                </p>
            )}

            {windows.length > 0 && (
                <div className="space-y-5">
                    {windows.map((win, idx) => (
                        <WindowRow key={win.type || idx} window={win} />
                    ))}
                </div>
            )}

            {!usageData.is_allowed && membershipExpired && (
                <UpgradeBanner
                    title="Gói hội viên đã hết hạn"
                    description="Vui lòng nâng cấp gói Hội viên để tiếp tục sử dụng và tối ưu trải nghiệm với Nebula."
                />
            )}

            {!usageData.is_allowed && !membershipExpired && noPlan && (
                <UpgradeBanner
                    title="Nâng cấp gói Hội viên để sử dụng Nebula AI"
                    description="Trải nghiệm trợ lý AI tài chính thông minh với nhiều tính năng chuyên sâu."
                />
            )}
        </div>
    );
};

export default UsagePanel;
